<section class="page-offset section-pad legal-page">
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-xl-9 legal-content-column">
        <div class="legal-hero soft-card bg-white p-4 p-md-5 mb-4">
          <p class="legal-eyebrow mb-2">{{ content.eyebrow }}</p>
          <h1 class="fw-bold mb-3">{{ content.title }}</h1>
          <p class="lead text-secondary mb-3">{{ content.subtitle }}</p>
          <div class="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
            <span class="small text-secondary">{{ content.lastUpdated }}</span>
            <a class="btn btn-sm btn-outline-primary" [routerLink]="alternateLink">{{ alternateLabel }}</a>
          </div>
        </div>

        @if (content.notice) {
          <div class="alert alert-warning" role="note">
            <i class="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>{{ content.notice }}
          </div>
        }

        <div class="legal-layout">
          <div #summarySlot class="legal-summary-slot" [style.min-height.px]="summaryPlaceholderHeight || null">
            <aside
              #summary
              class="legal-summary soft-card bg-white"
              [class.legal-summary--fixed]="summaryFixed"
              [style.top.px]="summaryFixed ? summaryTop : null"
              [style.left.px]="summaryFixed ? summaryLeft : null"
              [style.width.px]="summaryFixed ? summaryWidth : null"
              [attr.aria-label]="i18n.currentLanguage() === 'fr' ? 'Sommaire des sections juridiques' : 'Legal section summary'">
              <p class="legal-summary__eyebrow mb-2">{{ i18n.currentLanguage() === 'fr' ? 'Sommaire' : 'Summary' }}</p>
              <nav class="legal-summary__timeline">
                @for (section of content.sections; track section.title; let i = $index) {
                  <button type="button" class="legal-summary__item" [class.legal-summary__item--past]="sectionProgressState(i) === 'past'" [class.legal-summary__item--current]="sectionProgressState(i) === 'current'" (click)="scrollToSection($event, i)">
                    <span class="legal-summary__dot" [class.legal-summary__dot--past]="sectionProgressState(i) === 'past'" [class.legal-summary__dot--current]="sectionProgressState(i) === 'current'" aria-hidden="true"></span>
                    <span>{{ section.title }}</span>
                  </button>
                }
              </nav>
            </aside>
          </div>

          <article class="legal-document soft-card bg-white p-4 p-md-5">
            @for (section of content.sections; track section.title; let i = $index) {
              <section class="legal-section" [id]="sectionId(i)" [class.legal-section--featured]="section.featured">
              <h2 class="h4">{{ section.title }}</h2>
              @for (paragraph of section.paragraphs; track paragraph) {
                <p>{{ paragraph }}</p>
              }
              @if (section.links?.length) {
                <ul class="legal-links list-unstyled d-flex flex-wrap gap-2 mb-3">
                  @for (link of section.links; track link.href) {
                    <li>
                      <a class="btn btn-sm btn-outline-primary" [href]="link.href" [attr.target]="link.href.startsWith('http') ? '_blank' : null" [attr.rel]="link.href.startsWith('http') ? 'noopener noreferrer' : null">
                        {{ link.label }}
                      </a>
                    </li>
                  }
                </ul>
              }
              @if (section.oauthProviderLinks?.length) {
                <div class="legal-provider-table-wrap mb-3">
                  <table class="legal-provider-table table align-middle mb-0">
                    <thead>
                      <tr>
                        <th scope="col">{{ i18n.currentLanguage() === 'fr' ? 'Fournisseur' : 'Provider' }}</th>
                        <th scope="col">{{ i18n.currentLanguage() === 'fr' ? 'Confidentialité' : 'Privacy' }}</th>
                        <th scope="col">{{ i18n.currentLanguage() === 'fr' ? 'Conditions' : 'Terms' }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (providerLinks of section.oauthProviderLinks; track providerLinks.provider) {
                        <tr>
                          <th scope="row">
                            <span class="legal-provider-name">
                              <span class="legal-provider-icon legal-provider-icon-{{ providerLinks.provider }}">
                                @if (providerLinks.provider === 'google') {
                                  <svg class="google-g-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.89c2.27-2.09 3.53-5.17 3.53-8.87z" />
                                    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.9l-3.89-3c-1.08.72-2.46 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.95H1.26v3.09A12 12 0 0 0 12 24z" />
                                    <path fill="#FBBC05" d="M5.27 14.3A7.21 7.21 0 0 1 4.89 12c0-.8.14-1.58.38-2.3V6.61H1.26A12 12 0 0 0 0 12c0 1.94.46 3.77 1.26 5.39l4.01-3.09z" />
                                    <path fill="#EA4335" d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.45-3.45C17.96 1.16 15.24 0 12 0A12 12 0 0 0 1.26 6.61L5.27 9.7C6.22 6.86 8.87 4.75 12 4.75z" />
                                  </svg>
                                } @else if (providerLinks.provider === 'microsoft') {
                                  <svg class="microsoft-four-square-mark" viewBox="0 0 23 23" aria-hidden="true" focusable="false">
                                    <path fill="#f25022" d="M1 1h10v10H1z" />
                                    <path fill="#7fba00" d="M12 1h10v10H12z" />
                                    <path fill="#00a4ef" d="M1 12h10v10H1z" />
                                    <path fill="#ffb900" d="M12 12h10v10H12z" />
                                  </svg>
                                } @else if (providerLinks.provider === 'orcid') {
                                  <img class="orcid-id-mark" src="assets/ORCID-iD_icon_vector.svg" alt="" aria-hidden="true">
                                } @else {
                                  <i [class]="providerIconClass(providerLinks.provider)" aria-hidden="true"></i>
                                }
                              </span>
                              <span>{{ providerLinks.name }}</span>
                            </span>
                          </th>
                          <td><a [href]="providerLinks.privacy.href" target="_blank" rel="noopener noreferrer">{{ providerLinks.privacy.label }}</a></td>
                          <td><a [href]="providerLinks.terms.href" target="_blank" rel="noopener noreferrer">{{ providerLinks.terms.label }}</a></td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
              @if (pageKind === 'cgu' && section.title.startsWith('5.')) {
                <p class="mb-0">
                  <a routerLink="/privacy">{{ i18n.currentLanguage() === 'fr' ? 'Consulter la Politique de confidentialité' : 'Read the Privacy Policy' }}</a>
                </p>
              }
              @if (section.bullets?.length) {
                <ul>
                  @for (bullet of section.bullets; track bullet) {
                    <li>{{ bullet }}</li>
                  }
                </ul>
              }
              </section>
            }
          </article>
        </div>
      </div>
    </div>
  </div>
</section>